import { useId, useState } from 'react';
import { clsx } from '../../../ds/clsx';
import { Select, TextField } from '../../../ds/components';
import { useWorkspace } from '../../../lib/session';
import { useTasks } from '../../tasks/api';
import { addDays, isValidDayKey, isValidTime, todayKey } from '../../tasks/dates';
import type { DealNextStep } from '../types';
import { FieldBlock } from './bits';
import styles from './dialogs.module.css';

/**
 * The „Nächster Schritt" field group of every stage dialog (Deal-Pipeline 17–21, api-deals.md
 * `NextStep`): Wiedervorlage / Aufgabe / Termin buchen with the fields of the chosen step. It mirrors the
 * field set of the tasks module's `NextStepDialog` (Queue 07) without the lifecycle option, which a
 * deal transition never takes.
 */

export type NextStepKind = DealNextStep['kind'];

const KIND_LABELS: Record<Exclude<NextStepKind, 'existing'>, string> = {
  follow_up: 'Wiedervorlage',
  task: 'Aufgabe',
  book_appointment: 'Termin buchen',
};
const KINDS: readonly Exclude<NextStepKind, 'existing'>[] = ['follow_up', 'task', 'book_appointment'];

const DATE_REQUIRED = 'Bitte ein Datum wählen.';
const TIME_INVALID = 'Bitte eine Uhrzeit im Format HH:MM angeben.';
const TITLE_REQUIRED = 'Bitte einen Titel angeben.';

export interface NextStepErrors {
  dueOn?: string | undefined;
  dueTime?: string | undefined;
  title?: string | undefined;
}

export interface NextStepForm {
  kind: NextStepKind;
  setKind: (kind: NextStepKind) => void;
  dueOn: string;
  setDueOn: (value: string) => void;
  dueTime: string;
  setDueTime: (value: string) => void;
  assigneeId: string;
  setAssigneeId: (value: string) => void;
  note: string;
  setNote: (value: string) => void;
  title: string;
  setTitle: (value: string) => void;
  errors: NextStepErrors;
  /** Validates and returns the step for the API, or null (the errors are then set). */
  build: () => DealNextStep | null;
  reset: (defaults?: { kind?: NextStepKind; title?: string }) => void;
}

export function useNextStepForm(initialKind: NextStepKind = 'follow_up'): NextStepForm {
  const workspace = useWorkspace();
  const [kind, setKind] = useState<NextStepKind>(initialKind);
  const [dueOn, setDueOn] = useState(() => addDays(todayKey(), 1));
  const [dueTime, setDueTime] = useState('');
  const [assigneeId, setAssigneeId] = useState(workspace.user.id);
  const [note, setNote] = useState('');
  const [title, setTitle] = useState('');
  const [errors, setErrors] = useState<NextStepErrors>({});

  function build(): DealNextStep | null {
    const next: NextStepErrors = {};
    if (kind !== 'book_appointment' && kind !== 'existing') {
      if (!isValidDayKey(dueOn)) next.dueOn = DATE_REQUIRED;
      if (dueTime !== '' && !isValidTime(dueTime)) next.dueTime = TIME_INVALID;
    }
    if (kind === 'task' && title.trim() === '') next.title = TITLE_REQUIRED;
    setErrors(next);
    if (Object.keys(next).length > 0) return null;
    if (kind === 'existing') return { kind: 'existing' };
    if (kind === 'book_appointment') return { kind: 'book_appointment' };
    if (kind === 'task') {
      return {
        kind: 'task',
        title: title.trim(),
        dueOn,
        dueTime: dueTime === '' ? null : dueTime,
        assigneeId,
      };
    }
    return {
      kind: 'follow_up',
      dueOn,
      dueTime: dueTime === '' ? null : dueTime,
      assigneeId,
      note: note.trim() === '' ? null : note.trim(),
    };
  }

  function reset(defaults?: { kind?: NextStepKind; title?: string }) {
    setKind(defaults?.kind ?? initialKind);
    setDueOn(addDays(todayKey(), 1));
    setDueTime('');
    setAssigneeId(workspace.user.id);
    setNote('');
    setTitle(defaults?.title ?? '');
    setErrors({});
  }

  return {
    kind,
    setKind: (value) => {
      setKind(value);
      setErrors({});
    },
    dueOn,
    setDueOn,
    dueTime,
    setDueTime,
    assigneeId,
    setAssigneeId,
    note,
    setNote,
    title,
    setTitle,
    errors,
    build,
    reset,
  };
}

export interface NextStepFieldsProps {
  form: NextStepForm;
  /** Label row of the block: „Nächster Schritt" (default) or „Wiedervorlage" (Deal-Pipeline 18). */
  label?: string | undefined;
  /** „Pflichtfeld" (default) or „wird mit angelegt". */
  note?: string | undefined;
  /** Server error of the whole field (400 `fields.nextStep`). */
  error?: string | null | undefined;
  /** Only the kinds offered (the lost dialog offers the Wiedervorlage and the task). */
  kinds?: readonly Exclude<NextStepKind, 'existing'>[] | undefined;
  /**
   * The contact's open step (`stage-options.openStep`): offered first as „Weiter mit: <Titel> · <Fälligkeit>"
   * (kind `existing`, forward stage moves only — the caller passes it only then).
   */
  openStep?: { title: string; dueLabel: string } | null | undefined;
  disabled?: boolean | undefined;
}

/** The field group: kind switch + the fields of the chosen kind. */
export function NextStepFields({
  form,
  label = 'Nächster Schritt',
  note = 'Pflichtfeld',
  error,
  kinds = KINDS,
  openStep = null,
  disabled = false,
}: NextStepFieldsProps) {
  const workspace = useWorkspace();
  const tasks = useTasks(false);
  const groupId = useId();
  const today = todayKey();
  const assignees = tasks.data?.assignees;
  const me = `${workspace.user.firstName.charAt(0)}. ${workspace.user.lastName}`.trim();
  const assigneeOptions =
    assignees && assignees.length > 1
      ? assignees.map((user) => ({ value: user.id, label: user.name }))
      : null;

  const assigneeField = assigneeOptions ? (
    <Select
      label="Zuständig"
      size="compact"
      options={assigneeOptions}
      value={form.assigneeId}
      disabled={disabled}
      onChange={(event) => form.setAssigneeId(event.target.value)}
    />
  ) : (
    <TextField label="Zuständig" size="compact" value={me} readOnly />
  );

  return (
    <FieldBlock label={label} note={note} error={error}>
      <div className={styles.nextStep}>
        <div className={styles.nextStepKinds} role="radiogroup" aria-label={label} id={groupId}>
          {openStep ? (
            <button
              type="button"
              role="radio"
              aria-checked={form.kind === 'existing'}
              disabled={disabled}
              className={clsx(styles.kindButton, form.kind === 'existing' && styles.kindActive)}
              onClick={() => form.setKind('existing')}
            >
              Weiter mit: {openStep.title} · {openStep.dueLabel}
            </button>
          ) : null}
          {kinds.map((kind) => (
            <button
              key={kind}
              type="button"
              role="radio"
              aria-checked={form.kind === kind}
              disabled={disabled}
              className={clsx(styles.kindButton, form.kind === kind && styles.kindActive)}
              onClick={() => form.setKind(kind)}
            >
              {KIND_LABELS[kind]}
            </button>
          ))}
        </div>
        {form.kind === 'task' ? (
          <TextField
            label="Titel"
            size="compact"
            required
            value={form.title}
            error={form.errors.title}
            disabled={disabled}
            onChange={(event) => form.setTitle(event.target.value)}
          />
        ) : null}
        {form.kind === 'existing' ? (
          <div className={styles.nextStepHint}>
            Der offene Schritt bleibt der nächste Schritt des Kontakts. DAMOKLES legt keine neue Aufgabe an.
          </div>
        ) : null}
        {form.kind !== 'book_appointment' && form.kind !== 'existing' ? (
          <div className={styles.nextStepGrid}>
            <TextField
              label={form.kind === 'task' ? 'Fällig am' : 'Wiedervorlage am'}
              type="date"
              size="compact"
              required
              value={form.dueOn}
              min={today}
              error={form.errors.dueOn}
              disabled={disabled}
              onChange={(event) => form.setDueOn(event.target.value)}
            />
            <TextField
              label="Uhrzeit"
              type="time"
              size="compact"
              value={form.dueTime}
              error={form.errors.dueTime}
              disabled={disabled}
              onChange={(event) => form.setDueTime(event.target.value)}
            />
            {assigneeField}
          </div>
        ) : null}
        {form.kind === 'follow_up' ? (
          <TextField
            label="Notiz zur Wiedervorlage"
            size="compact"
            optional
            value={form.note}
            disabled={disabled}
            onChange={(event) => form.setNote(event.target.value)}
          />
        ) : null}
        {form.kind === 'book_appointment' ? (
          <div className={styles.nextStepHint}>
            DAMOKLES legt die Zwischenaufgabe „Termin buchen“ an und öffnet nach dem Speichern die
            Terminbuchung in der Kontaktakte.
          </div>
        ) : null}
      </div>
    </FieldBlock>
  );
}
